import React, { createContext, useContext, useState, useCallback } from 'react'
import { ApiBaseProvider } from './api-base-provider'
import { V2GlobalHeader } from './v2-global-header'
import { RemoteLoginDialog } from './remote-login-dialog'
import { useWistorAuth } from '../../api/auth'
import '../v2-theme.css'

// ─── Theme Context ────────────────────────────────────
type ThemeMode = 'light' | 'dark'

type ThemeContextValue = {
  mode: ThemeMode
  toggle: () => void
}

const THEME_KEY = 'wistor-v2-theme'

function loadTheme(): ThemeMode {
  try {
    const stored = localStorage.getItem(THEME_KEY)
    if (stored === 'dark' || stored === 'light') return stored
  } catch {}
  // Default to dark (matches our preview aesthetic)
  return 'dark'
}

const ThemeContext = createContext<ThemeContextValue>({ mode: 'dark', toggle: () => {} })

export function useV2Theme() {
  return useContext(ThemeContext)
}

// ─── Layout Component ─────────────────────────────────
export function V2Layout({ children }: { children: React.ReactNode }) {
  const [mode, setMode] = useState<ThemeMode>(loadTheme)
  const auth = useWistorAuth()

  const toggle = useCallback(() => {
    setMode(prev => {
      const next = prev === 'dark' ? 'light' : 'dark'
      try { localStorage.setItem(THEME_KEY, next) } catch {}
      return next
    })
  }, [])

  // Unauthorized routes render the login page as `children`; it brings its own
  // chrome, and the platform nav would be misleading there.
  const showHeader = auth.status === 'authorized'

  return (
    <ThemeContext.Provider value={{ mode, toggle }}>
      <ApiBaseProvider>
        <div className={`wistor-v2 ${mode === 'dark' ? 'v2-dark' : ''}`}>
          {showHeader && <V2GlobalHeader />}
          <main className="v2-main">
            {children}
          </main>
          <RemoteLoginDialog />
        </div>
      </ApiBaseProvider>
    </ThemeContext.Provider>
  )
}
